import { useGetQuizMarked } from '@repo/api/quiz/useGetQuizMarked';
import { CircleSlash } from 'lucide-react';
import { useState } from 'react';

import QuizHeader from '@/components/features/quiz/header/QuizHeader';
import QuizResultContent from '@/components/features/quiz/results/QuizResultContent';
import QuizResultData from '@/components/features/quiz/results/QuizResultData';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { ScrollProvider } from '@/components/ui/scroll';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuizParams } from '@/hooks/quiz';

export default function QuizResultPage() {
  const { id, attemptId } = useQuizParams();
  const { data, isError, isPending, errorMessage } = useGetQuizMarked(id, attemptId);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  if (isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className={'z-0'} />
          <FeaturedIcon size={'lg'} className={'z-10'}>
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="base-direction-col">
        {/* Header skeleton */}
        <div className="border-border-primary flex w-full flex-col gap-2 rounded-2xl border p-4">
          <Skeleton className="h-5 w-12 rounded-sm" />
          <Skeleton className="my-2 h-4 w-24 rounded-sm" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
          </div>
        </div>
        <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
          {/* Results panel skeleton */}
          <div className="w-full rounded-2xl border lg:w-xs lg:min-w-xs">
            <div className="w-full border-b p-4">
              <Skeleton className="h-6 w-16" />
            </div>
            <div className="flex flex-row flex-wrap gap-2 p-3 text-nowrap md:p-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-16 rounded-sm" />
              ))}
            </div>
          </div>
          {/* Content skeleton */}
          <div className="w-full space-y-4">
            {Array.from({ length: 2 }).map((_, sectionIdx) => (
              <div key={sectionIdx} className="rounded-2xl border p-4">
                <Skeleton className="mb-4 h-6 w-1/3" />
                {Array.from({ length: 3 }).map((_, questionIdx) => (
                  <div key={questionIdx} className="mb-6">
                    <Skeleton className="mb-2 h-4 w-1/2" />
                    <Skeleton className="mb-2 h-10 w-full" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="base-direction-col">
      <QuizHeader quiz={data.quiz} />
      <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
        <ScrollProvider
          options={{
            scrollIntoViewOptions: { behavior: 'smooth' },
            threshold: 0.01,
          }}
        >
          <div className="h-fit min-h-0 w-full lg:sticky lg:top-4 lg:w-xs lg:min-w-xs">
            <QuizResultData quiz={data.quiz} attempt={data.attempt} selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx} />
          </div>
          <div className="w-full overflow-auto">
            <QuizResultContent quiz={data.quiz} attempt={data.attempt} selectedIdx={selectedIdx} />
          </div>
        </ScrollProvider>
      </div>
    </div>
  );
}
